import React, { memo, useMemo } from 'react'

import {
  array, bool, object, shape, string,
} from 'prop-types'

import ModelCard from 'Pods/Shared/ModelCard/ModelCard'

import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'

// Mirrors the ListingPage default (all sections visible); cgtStandard tracks the fast-page flag.
const CARD_VISIBILITY_SETTINGS = {
  title: true,
  type: true,
  price: true,
  format: true,
  quickActionsOnHover: true,
  preview: true,
  cgtStandard: false,
  suggestions: true,
}
const CARD_VISIBILITY_SETTINGS_WITH_CGT = { ...CARD_VISIBILITY_SETTINGS, cgtStandard: true }

// ModelCard is memoized, but the grid-wide inputs below (cart, cart item ids, wishlist, viewer and
// subscription gates) arrive client-side after hydration and change identity for every card at
// once. Each is narrowed here to the value this card renders from, so an arrival only re-renders
// the cards it affects.
function ResultCard({
  model,
  cart,
  cartItemIds,
  wishListItems,
  viewerIsAdult,
  showCgtVerification,
  subscriptionBadgesEnabled,
  subscriptionFlowEnabled,
  premiumListingLabelEnabled,
  showGallery = null,
  ...cardProps
}) {
  const { attributes = EMPTY_OBJECT } = model
  const modelId = Number(model.id)
  // The open gallery hangs off an anchor card but shows another model, so that card needs the
  // full lists and the real verification flag.
  const isGalleryOpen = !!showGallery

  const cartId = cartItemIds.find(({ itemId }) => Number(itemId) === modelId)?.cartId
  const isOnWishlist = wishListItems.includes(modelId)
  const ownCartItemIds = useMemo(() => (cartId ? [{ cartId, itemId: modelId }] : EMPTY_ARRAY), [cartId, modelId])
  const ownWishListItems = useMemo(() => (isOnWishlist ? [modelId] : EMPTY_ARRAY), [isOnWishlist, modelId])

  // ResolveProductPricing only reads the cart for sale-off or CLD items.
  const isPricedByCart = !!(attributes.isSaleOffApplicable || attributes.isCLDApplicable)
  // ModelCardVerifiedTag renders nothing for an unverified model.
  const isCgtVerificationShown = showCgtVerification && (!!attributes.modelInfo?.isCgtVerified || isGalleryOpen)

  return (
    <ModelCard
      {...model}
      {...cardProps}
      cart={isPricedByCart ? cart : EMPTY_OBJECT}
      cartItemIds={isGalleryOpen ? cartItemIds : ownCartItemIds}
      wishListItems={isGalleryOpen ? wishListItems : ownWishListItems}
      viewerIsAdult={!!attributes.primaryImage?.isAdultContent && viewerIsAdult}
      showCgtVerification={isCgtVerificationShown}
      cardVisibilitySettings={isCgtVerificationShown ? CARD_VISIBILITY_SETTINGS_WITH_CGT : CARD_VISIBILITY_SETTINGS}
      subscriptionBadgesEnabled={subscriptionBadgesEnabled && !!attributes.subscriptionPotential}
      subscriptionFlowEnabled={subscriptionFlowEnabled && !!attributes.subscriptionSubscribed}
      premiumListingLabelEnabled={premiumListingLabelEnabled && attributes.subscriptionTierBand === 'premium'}
      showGallery={showGallery}
    />
  )
}

ResultCard.propTypes = {
  model: shape({
    id: string.isRequired,
    attributes: object,
  }).isRequired,
  cart: object.isRequired,
  cartItemIds: array.isRequired,
  wishListItems: array.isRequired,
  viewerIsAdult: bool.isRequired,
  showCgtVerification: bool.isRequired,
  subscriptionBadgesEnabled: bool.isRequired,
  subscriptionFlowEnabled: bool.isRequired,
  premiumListingLabelEnabled: bool.isRequired,
  showGallery: object,
}

export default memo(ResultCard)
